import { useSendContactInfo } from "@/api/free-assessment/useSendContactInfo";
import InputGroup from "@/components/InputGroup";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import {
  getAssessmentPaperId,
  getAssessmentResult,
  getUserInfo,
} from "@/lib/freeAssessment";
import { emailPattern } from "@/lib/regexPattern";
import toast from "@/lib/toast";
import { useTurnstileToken } from "@/hooks/useTurnstileToken";
import {
  ContactForm,
} from "@/schema/free-assessment/ContactForm";
import {
  CheckmarkRegular,
  DismissRegular,
  SpinnerIosRegular,
} from "@fluentui/react-icons";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

const Spinner = () => <SpinnerIosRegular className="h-5 w-5 animate-spin" />;
function RequestPaidAssessmentDialog() {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const info = getUserInfo();
  const assessmentPaperId = getAssessmentPaperId();
  const result = getAssessmentResult();

  if (!assessmentPaperId) {
    navigate("/free-assessment/info");
    return null;
  }

  var termsOfService = "https://koyouassess.biz/inquiry.html";
  var privacyPolicy = "https://koyouassess.biz/privacy_policy.html";
  if (info?.refererId) {
    termsOfService = termsOfService + "?refererId=" + info.refererId;
    privacyPolicy = privacyPolicy + "?refererId=" + info.refererId;
  }

  const [open, setOpen] = useState(false);
  const {
    ref: turnstileRef,
    token: turnstileToken,
    isReady: isTurnstileReady,
    isLoading: isTurnstileLoading,
    error: turnstileError,
    reset: resetTurnstile,
  } = useTurnstileToken();
  const { trigger, isMutating } = useSendContactInfo({
    onSuccess: () => {
      toast.custom((t) => (
        <div className="flex w-[472px] justify-between rounded-lg bg-green-100 p-4">
          <div className="flex items-center gap-3">
            <CheckmarkRegular className="text-green-600" />
            <div className="flex flex-col text-green-600">
              <span className="text-base font-medium">
                {translate("contact_info_submitted")}
              </span>
              <span className=" ">{translate("check_email_confirmation")}</span>
            </div>
          </div>
          <DismissRegular
            className="cursor-pointer"
            onClick={() => toast.dismiss(t.id)}
          />
        </div>
      ));
      setOpen(false);
    },
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ContactForm>({
    defaultValues: info,
    mode: "onSubmit",
  });

  return (
    <div>
      <Dialog open={open} onOpenChange={setOpen}>
        <Button onClick={() => setOpen(true)}>
          {translate("apply-regular-assessment-title")}
        </Button>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{translate("apply-regular-assessment")}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[520px]">
            <form
              onSubmit={handleSubmit(
                async ({
                  firstName,
                  lastName,
                  companyName,
                  refererId,
                  ...rest
                }) => {
                  if (result) {
                    const { total, categories } = result;
                    const freeCategoryResults = categories.map((category) => {
                      const { id, points, subCategories } = category;
                      const freeSubCategoryResults = subCategories.map(
                        (subCategory) => {
                          const { id, points } = subCategory;
                          return {
                            sub_category_id: id,
                            score: typeof points === "number" ? points : 0,
                          };
                        },
                      );
                      return {
                        main_category_id: id,
                        score: typeof points === "number" ? points : 0,
                        free_sub_category_results: freeSubCategoryResults,
                      };
                    });

                    if (!turnstileToken) {
                      toast.error(translate("turnstile-verification-failed"));
                      return;
                    }

                    try {
                      await trigger({
                        assessment_paper_id: assessmentPaperId,
                        client_name: `${firstName} ${lastName}`,
                        company_name: companyName,
                        referer_id: refererId ?? "",
                        has_inquiry_request: true,
                        ...rest,
                        score: typeof total === "number" ? total : 0,
                        free_category_results: freeCategoryResults,
                        cf_turnstile_response: turnstileToken,
                      });
                    } finally {
                      resetTurnstile();
                    }
                  }
                },
              )}
            >
              <div className="grid-col-2 grid w-full gap-6 px-6 pb-5 pt-4">
                <div className="col-span-2 rounded-lg bg-cool-gray-50 p-4">
                  {translate("applyForm.description1")} <br />
                  <br />
                  {translate("applyForm.description2")} <br />
                  <br />
                  {translate("applyForm.description3")}
                </div>
                <InputGroup
                  label={translate("company-or-belongs-name")}
                  name="company"
                  error={errors.companyName?.message}
                  register={register("companyName", {
                    required: translate("company-name-required"),
                  })}
                  className="col-span-2"
                />
                <InputGroup
                  label={translate("last-name")}
                  name="lastName"
                  className="col-span-2 sm:col-span-1"
                  error={errors.lastName?.message}
                  register={register("lastName", {
                    required: translate("last-name-required"),
                  })}
                />
                <InputGroup
                  label={translate("first-name")}
                  name="firstName"
                  className="col-span-2 sm:col-span-1"
                  error={errors.firstName?.message}
                  register={register("firstName", {
                    required: translate("first-name-required"),
                  })}
                />
                <InputGroup
                  label={translate("email")}
                  name="email"
                  error={errors.email?.message}
                  register={register("email", {
                    required: translate("email-required"),
                    pattern: {
                      value: emailPattern,
                      message: translate("valid-email"),
                    },
                  })}
                  className="col-span-2"
                  type="email"
                />
                <InputGroup
                  label={translate("phone")}
                  name="phone"
                  error={errors.phone?.message}
                  register={register("phone", {
                    required: translate("phone-required"),
                  })}
                  className="col-span-2"
                  type="tel"
                />
                <Controller
                  control={control}
                  name="message"
                  render={({ field: { onChange, value } }) => (
                    <>
                      <Label
                        label={translate("question-and-request")}
                        htmlFor="message"
                      />
                      <Textarea
                        id="message"
                        className="col-span-2"
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                      />
                    </>
                  )}
                />
                <div className="cf-turnstile-container col-span-2">
                  <div ref={turnstileRef} className="cf-turnstile" />
                </div>
                {turnstileError && (
                  <p className="col-span-2 text-center text-sm text-red-600">
                    {translate("turnstile-verification-failed")}
                  </p>
                )}
                <Button
                  isLoading={isMutating}
                  LoadingComponent={<Spinner />}
                  type="submit"
                  className="col-span-2"
                  disabled={
                    isMutating ||
                    !turnstileToken ||
                    !isTurnstileReady ||
                    isTurnstileLoading
                  }
                >
                  {translate("apply")}
                </Button>
                <div className="col-span-2 text-center text-xs text-cool-gray-500">
                  <span>{translate("agree-to")} </span>
                  <Link
                    to={termsOfService}
                    className="text-cyan-600"
                    target="_blank"
                  >
                    {translate("terms-of-service")}
                  </Link>
                  <span> {translate("and")} </span>
                  <Link
                    to={privacyPolicy}
                    className="text-cyan-600"
                    target="_blank"
                  >
                    {translate("privacy-policy")}
                  </Link>
                  <span>{translate("agree-end")}</span>
                </div>
              </div>
            </form>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default RequestPaidAssessmentDialog;
